'use client';

import InlineStyle from '@/components/InlineStyle';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, Button } from '@/components/ui';
import { ShieldCheck, BadgeCheck, Unlock, Zap } from 'lucide-react';
import { useTranslation } from "@/hooks/useTranslation";
const TRUST_BADGES = [{
  Icon: ShieldCheck,
  label: 'No surprise add-ons'
}, {
  Icon: BadgeCheck,
  label: '100% Transparent Pricing'
}, {
  Icon: Unlock,
  label: 'No long lock-ins'
}, {
  Icon: Zap,
  label: 'No setup fee'
}];
const AI_PRO = {
  name: 'AI Pro',
  includes: 'Plan includes:',
  core: ['Real-Time Monitoring', 'ContextualPulse™', 'ResponseGenie™', 'Unified Ticketing System', 'Publishing & Response Control', 'GMB Review Management', 'Performance Dashboards', 'Onboarding & Success Support'],
  features: ['Track every brand mention, review, or tag as it happens.', 'Understand the tone and motivation behind every interaction.', 'Manage queries across social, email, reviews, and chat from one place.', 'Schedule, approve, and respond with brand governance.', 'Respond to and analyse reviews across Google locations.', 'Get metrics that matter — response times, volumes, NPS, CSAT, and more.', 'We don\'t just hand you the tool, we help you master it.']
};
const AI_PRO_MAX = {
  name: 'AI Pro Max',
  note: 'Plan includes all AI Pro features plus:',
  extra: ['SignalSense™', 'AgentIQ™', 'Location Intelligence', 'Instant Conversation Summaries', 'Smart Auto-Tagging', 'Workflow Automation'],
  features: ['Compare outlet-level performance and customer feedback.', 'Craft personalised replies using your KnowledgeBase, tone, and past conversations.', 'Real-time coaching that improves response quality and predicts CSAT drops.', 'Save agent time by summarising long queries instantly.', 'Detect vitality risks, PR spikes, or campaign buzz before they escalate.', 'Auto-label conversations by emotion, urgency, and theme.', 'Live audit of tickets and team performance across channels.']
};
export default function PricingPage() {
  const {
    t
  } = useTranslation();
  return <>
      {/* Hero — redesigned */}
      <section className="pricing-hero">
        <div className="pricing-hero__blob pricing-hero__blob--1" aria-hidden="true" />
        <div className="pricing-hero__blob pricing-hero__blob--2" aria-hidden="true" />
        <div className="pricing-hero__grid" aria-hidden="true" />

        <div className="container pricing-hero__inner">
          <span className="pricing-hero__kicker">
            <span className="pricing-hero__kickerDot" aria-hidden="true" />
            {t("nav.pricing")}
          </span>

          <h1 className="pricing-hero__h1">
            {t("pricing.plans_curated_for_leaders_who_think")}{' '}
            <span className="pricing-hero__accent">{t("pricing.10_steps_ahead")}</span>
          </h1>

          <p className="pricing-hero__sub">
            {t("pricing.choose_the_plan_that_best_fits_your")} <strong>{t("pricing.full_access_full_clarity_full_suppo")}</strong>
          </p>

          <div className="pricing-hero__badges">
            {TRUST_BADGES.map(({
            Icon,
            label
          }) => <div key={label} className="pricing-hero__badge">
                <Icon size={18} strokeWidth={2.2} className="pricing-hero__badgeIcon" />
                <span className="pricing-hero__badgeLabel">{label}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section style={{
      background: '#F8FAFF',
      padding: 'var(--section-pad)'
    }}>
        <div className="container">
          <div style={{
          textAlign: 'center',
          marginBottom: 52
        }}>
            <h2 className="lb-h2" style={{
            marginBottom: 12
          }}>{t("pricing.locobuzz_pricing_plans")}</h2>
            <p style={{
            fontSize: 16,
            color: '#4B5563',
            maxWidth: 560,
            margin: '0 auto'
          }}>
              {t("pricing.power_your_cx_and_marketing_with_in")}
            </p>
          </div>
          <div className="pricing-plans-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 28,
          maxWidth: 900,
          margin: '0 auto',
          alignItems: 'start'
        }}>
            {/* AI Pro */}
            <div style={{
            background: '#fff',
            border: '1.5px solid #E5E7EB',
            borderRadius: 20,
            padding: 36,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
          }}>
              <h2 style={{
              fontFamily: "'Inter',sans-serif",
              fontWeight: 800,
              fontSize: 28,
              color: '#111827',
              marginBottom: 8
            }}>{t("pricing.ai_pro")}</h2>
              <div style={{
              fontSize: 14,
              color: '#6B7280',
              marginBottom: 20
            }}>{AI_PRO.includes}</div>
              {AI_PRO.core.map((item, i) => <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 10
            }}>
                  <div style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#EBF4FF',
                border: '1.5px solid #0077FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                    <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="#0077FF" strokeWidth="2.5"><polyline points="2,6 5,9 10,3" /></svg>
                  </div>
                  <span style={{
                fontSize: 14,
                color: '#374151',
                fontWeight: 500
              }}>{item}</span>
                </div>)}
              <hr style={{
              border: 'none',
              borderTop: '1px solid #F3F4F6',
              margin: '20px 0'
            }} />
              <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#9CA3AF',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              marginBottom: 12
            }}>{t("pricing.features")}</div>
              {AI_PRO.features.map((f, i) => <div key={i} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              marginBottom: 8
            }}>
                  <span style={{
                color: '#0077FF',
                fontSize: 14,
                flexShrink: 0,
                marginTop: 1
              }}>•</span>
                  <span style={{
                fontSize: 13,
                color: '#4B5563',
                lineHeight: 1.6
              }}>{f}</span>
                </div>)}
              <div style={{
              marginTop: 28
            }}>
                <Button href="#book-demo" variant="cta" size="lg">{t("pricing.get_a_quote")}</Button>
              </div>
            </div>

            {/* AI Pro Max — same white card surface as AI Pro; highlighted via a
                brand-blue accent ring + "Recommended" pill so it still reads as
                the upsell tier. */}
            <div style={{
            background: '#fff',
            border: '1.5px solid #0077FF',
            borderRadius: 20,
            padding: 36,
            boxShadow: '0 20px 60px rgba(0,119,255,0.16)',
            position: 'relative',
            overflow: 'hidden'
          }}>
              <div style={{
              position: 'absolute',
              top: -30,
              right: -30,
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'rgba(0,119,255,0.05)'
            }} />
              <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '5px 12px',
              borderRadius: 100,
              background: 'linear-gradient(135deg,#0098FF,#0057D4)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              marginBottom: 12
            }}>{t("pricing.recommended")}</span>
              <h2 style={{
              fontFamily: "'Inter',sans-serif",
              fontWeight: 800,
              fontSize: 28,
              color: '#111827',
              marginBottom: 8
            }}>{t(AI_PRO_MAX.name)}</h2>
              <div style={{
              fontSize: 14,
              color: '#6B7280',
              marginBottom: 20
            }}>{AI_PRO_MAX.note}</div>
              {AI_PRO_MAX.extra.map((item, i) => <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 10
            }}>
                  <div style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: '#EBF4FF',
                border: '1.5px solid #0077FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                    <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="#0077FF" strokeWidth="2.5"><polyline points="2,6 5,9 10,3" /></svg>
                  </div>
                  <span style={{
                fontSize: 14,
                color: '#374151',
                fontWeight: 500
              }}>{item}</span>
                </div>)}
              <hr style={{
              border: 'none',
              borderTop: '1px solid #F3F4F6',
              margin: '20px 0'
            }} />
              <div style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#9CA3AF',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              marginBottom: 12
            }}>{t("pricing.features")}</div>
              {AI_PRO_MAX.features.map((f, i) => <div key={i} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              marginBottom: 8
            }}>
                  <span style={{
                color: '#0077FF',
                fontSize: 14,
                flexShrink: 0,
                marginTop: 1
              }}>•</span>
                  <span style={{
                fontSize: 13,
                color: '#4B5563',
                lineHeight: 1.6
              }}>{f}</span>
                </div>)}
              <div style={{
              marginTop: 28
            }}>
                <Button href="#book-demo" variant="cta" size="lg">{t("pricing.get_a_quote")}</Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Need Help */}
      <section style={{
      background: '#F8FAFF',
      padding: 'var(--section-pad-tight)',
      textAlign: 'center'
    }}>
        <div className="container" style={{
        maxWidth: 500
      }}>
          <h2 className="lb-h2" style={{
          marginBottom: 12
        }}>{t("pricing.need_help_choosing")}</h2>
          <p style={{
          fontSize: 16,
          color: '#4B5563',
          marginBottom: 28
        }}>{t("pricing.lets_walk_through_your_needs_togeth")}</p>
          <BookDemoButton label={t("pricing.book_a_free_demo")} />
        </div>
      </section>

      <BookDemoSection />

      <InlineStyle css={`
        /* ── Pricing hero ─────────────────────────────────────────── */
        .pricing-hero {
          position: relative;
          overflow: hidden;
          padding: 140px 24px 96px;
          text-align: center;
          font-family: var(--font-inter), system-ui, sans-serif;
          background: linear-gradient(180deg, #FFFFFF 0%, #F4F7FF 100%);
        }
        .pricing-hero__blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.45;
          pointer-events: none;
          animation: pricingFloat 22s ease-in-out infinite;
        }
        .pricing-hero__blob--1 { width: 480px; height: 480px; top: -120px; left: -100px; background: rgba(0, 152, 255, 0.42); }
        .pricing-hero__blob--2 { width: 420px; height: 420px; top: 60px; right: -120px; background: rgba(0, 87, 212, 0.30); animation-delay: -10s; }
        @keyframes pricingFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(28px, -22px) scale(1.06); }
        }
        .pricing-hero__grid {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background-image: radial-gradient(rgba(0, 119, 255, 0.06) 1px, transparent 1px);
          background-size: 32px 32px;
          mask-image: radial-gradient(ellipse 60% 60% at 50% 40%, #000 0%, transparent 80%);
          -webkit-mask-image: radial-gradient(ellipse 60% 60% at 50% 40%, #000 0%, transparent 80%);
        }

        .pricing-hero__inner { position: relative; z-index: 1; max-width: 900px; }

        .pricing-hero__kicker {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 16px;
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(0, 119, 255, 0.25);
          border-radius: 100px;
          font-size: 12px;
          font-weight: 800;
          color: #0077FF;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          margin-bottom: 24px;
          box-shadow: 0 6px 18px rgba(0, 119, 255, 0.10);
        }
        .pricing-hero__kickerDot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #0077FF;
          box-shadow: 0 0 0 3px rgba(0, 119, 255, 0.18);
        }

        .pricing-hero__h1 {
          font-family: 'Inter Tight', var(--font-inter), sans-serif;
          font-size: clamp(36px, 5.5vw, 64px);
          font-weight: 800;
          color: #0D1117;
          letter-spacing: -0.035em;
          line-height: 1.08;
          margin: 0 0 24px;
          max-width: 820px;
          margin-left: auto;
          margin-right: auto;
        }
        .pricing-hero__accent {
          background: linear-gradient(135deg, #0098FF 0%, #0057D4 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          font-style: italic;
        }

        .pricing-hero__sub {
          font-size: 17px;
          color: #4B5563;
          line-height: 1.75;
          margin: 0 auto;
          max-width: 720px;
        }
        .pricing-hero__sub strong { color: #0D1117; font-weight: 700; }

        /* Trust badges — single-line chips arranged in a 4-col row.
           No card chrome — just an inline icon + label, evenly distributed. */
        .pricing-hero__badges {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, auto));
          justify-content: center;
          column-gap: 36px;
          row-gap: 14px;
          margin: 44px auto 0;
        }
        .pricing-hero__badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }
        .pricing-hero__badgeIcon {
          color: #0077FF;
          flex-shrink: 0;
        }
        .pricing-hero__badgeLabel {
          font-size: 14px;
          font-weight: 600;
          color: #1F2937;
          letter-spacing: -0.005em;
        }

        @media (max-width: 760px) {
          .pricing-hero { padding: 100px 18px 72px; }
          .pricing-hero__badges {
            grid-template-columns: repeat(2, minmax(0, auto));
            column-gap: 28px;
            margin-top: 36px;
          }
          .pricing-hero__badgeLabel { font-size: 13.5px; }
        }
        @media (max-width: 420px) {
          .pricing-hero__badges {
            grid-template-columns: 1fr;
            justify-items: center;
            row-gap: 12px;
          }
        }

        /* Pricing-card CTAs. Bespoke buttons so we control the hover state
           explicitly — the legacy lb-btn-secondary turned text invisible on
           hover when the inline color stayed blue and the background tinted
           blue too. Both variants now resolve to white text on solid brand
           background on hover. */
        .pricing-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 13px 24px;
          border-radius: 10px;
          font-family: var(--font-inter-tight), system-ui, sans-serif;
          font-size: 15px;
          font-weight: 700;
          text-decoration: none;
          cursor: pointer;
          transition: background 0.2s, color 0.2s, border-color 0.2s, transform 0.18s, box-shadow 0.2s;
        }
        .pricing-cta--outline {
          background: #fff;
          color: #0077FF;
          border: 1.5px solid #0077FF;
        }
        .pricing-cta--outline:hover {
          background: #0077FF;
          color: #fff;
          transform: translateY(-1px);
          box-shadow: 0 8px 20px rgba(0,119,255,0.30);
        }
        .pricing-cta--solid {
          background: linear-gradient(135deg, #0098FF, #0057D4);
          color: #fff;
          border: none;
          box-shadow: 0 8px 20px rgba(0,119,255,0.30);
        }
        .pricing-cta--solid:hover {
          color: #fff;
          transform: translateY(-1px);
          box-shadow: 0 14px 28px rgba(0,119,255,0.42);
        }

        @media (max-width: 800px) {
          .pricing-plans-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
          .pricing-plans-grid > div { padding: 28px !important; }
        }
      `} />
    </>;
}